import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Fuel, Gauge, AlertTriangle, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function FuelPanel({ job, canEdit, onChanged }) {
  const [vehicles, setVehicles] = useState([]);
  const [km, setKm] = useState({
    vehicle_id: job.vehicle_id || "none",
    km_start: job.km_start ?? "",
    km_end: job.km_end ?? "",
  });
  const [refuel, setRefuel] = useState({ cost_eur: "", date: new Date().toISOString().slice(0, 10), note: "", file: null });
  const fileRef = useRef(null);
  const [savingKm, setSavingKm] = useState(false);
  const [savingRefuel, setSavingRefuel] = useState(false);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);

  const saveKm = async () => {
    setSavingKm(true);
    try {
      const body = {
        vehicle_id: km.vehicle_id === "none" ? null : km.vehicle_id,
        km_start: km.km_start === "" ? null : Number(km.km_start),
        km_end: km.km_end === "" ? null : Number(km.km_end),
      };
      const { data } = await api.put(`/jobs/${job.id}/km`, body);
      onChanged?.(data);
      toast.success("Dati mezzo salvati");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingKm(false); }
  };

  const addRefuel = async (e) => {
    e.preventDefault();
    if (!refuel.cost_eur) return toast.error("Compila l'importo");
    setSavingRefuel(true);
    try {
      const fd = new FormData();
      if (refuel.file) fd.append("file", refuel.file);
      const { data } = await api.post(`/jobs/${job.id}/refuels`, fd, {
        params: {
          cost_eur: Number(refuel.cost_eur),
          date: refuel.date,
          note: refuel.note,
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChanged?.(data);
      toast.success("Rifornimento aggiunto");
      setRefuel({ cost_eur: "", date: new Date().toISOString().slice(0, 10), note: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingRefuel(false); }
  };

  const removeRefuel = async (rid) => {
    if (!confirm("Eliminare questo rifornimento?")) return;
    try {
      await api.delete(`/jobs/${job.id}/refuels/${rid}`);
      const { data } = await api.get(`/jobs/${job.id}`);
      onChanged?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const anomaly = job.fuel_anomaly;
  const refuels = job.refuels || [];
  const totalCost = refuels.reduce((s, r) => s + Number(r.cost_eur || 0), 0);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="fuel-panel">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
        <Gauge className="w-4 h-4 text-sky-600" /> Mezzo & Carburante
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label>Mezzo utilizzato</Label>
          <Select value={km.vehicle_id} onValueChange={(v) => setKm({ ...km, vehicle_id: v })} disabled={!canEdit}>
            <SelectTrigger data-testid="fuel-vehicle-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Nessuno —</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Km partenza</Label>
          <Input type="number" value={km.km_start} onChange={(e) => setKm({ ...km, km_start: e.target.value })} disabled={!canEdit} data-testid="fuel-km-start-input" />
        </div>
        <div><Label>Km arrivo</Label>
          <Input type="number" value={km.km_end} onChange={(e) => setKm({ ...km, km_end: e.target.value })} disabled={!canEdit} data-testid="fuel-km-end-input" />
        </div>
      </div>
      {canEdit && (
        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {km.km_start !== "" && km.km_end !== "" ? (
              <>Km percorsi: <span className="font-bold font-mono text-slate-900">{Number(km.km_end) - Number(km.km_start)}</span></>
            ) : "Inserisci km partenza e arrivo"}
          </div>
          <Button onClick={saveKm} disabled={savingKm} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-km-button">
            {savingKm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salva dati mezzo
          </Button>
        </div>
      )}

      {/* Refuels */}
      <div className="mt-6 pt-5 border-t border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-slate-900 flex items-center gap-2">
            <Fuel className="w-4 h-4 text-sky-600" /> Rifornimenti
            <span className="text-xs font-mono text-slate-400">({refuels.length})</span>
          </div>
          <div className="text-xs font-mono text-slate-500">
            Totale: <span className="font-bold">{fmt(totalCost)}</span>
          </div>
        </div>
        {refuels.length > 0 && (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl mb-3">
            {refuels.map((r) => (
              <li key={r.id} className="px-3 py-2.5 flex items-center gap-3 text-sm">
                <Fuel className="w-4 h-4 text-sky-600 shrink-0" />
                <div className="flex-1">
                  <div className="font-mono font-bold">{fmt(r.cost_eur)}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {r.date} · {r.uploaded_by_name}{r.note && ` · ${r.note}`}
                  </div>
                </div>
                {r.receipt_path && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const resp = await api.get(`/jobs/${job.id}/refuels/${r.id}/download`, { responseType: "blob" });
                        const url = URL.createObjectURL(resp.data);
                        window.open(url, "_blank");
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (err) {
                        toast.error(formatApiError(err));
                      }
                    }}
                    className="text-xs text-sky-600 hover:text-sky-700 font-semibold"
                  >Scontrino</button>
                )}
                <button onClick={() => removeRefuel(r.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <form onSubmit={addRefuel} className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="refuel-form">
            <div><Label className="text-xs">Importo (€)</Label>
              <Input type="number" step="0.01" value={refuel.cost_eur} onChange={(e) => setRefuel({ ...refuel, cost_eur: e.target.value })} required data-testid="refuel-cost-input" />
            </div>
            <div><Label className="text-xs">Data</Label>
              <Input type="date" value={refuel.date} onChange={(e) => setRefuel({ ...refuel, date: e.target.value })} required />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Label className="text-xs">Scontrino</Label>
              <input ref={fileRef} type="file" accept="image/*,application/pdf"
                onChange={(e) => setRefuel({ ...refuel, file: e.target.files?.[0] || null })}
                className="block w-full text-xs mt-1" data-testid="refuel-file-input" />
            </div>
            <Button type="submit" disabled={savingRefuel} className="col-span-2 md:col-span-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-refuel-button">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </form>
        )}
      </div>

      {/* Anomaly carburante */}
      {anomaly && (
        <div className="mt-5 border-2 border-rose-200 bg-rose-50 rounded-xl p-4 flex items-start gap-3" data-testid="fuel-anomaly-alert">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-rose-900">⚠️ Anomalia carburante rilevata</div>
            <div className="text-rose-800 mt-1 text-xs font-mono space-y-0.5">
              <div>Mezzo: {anomaly.vehicle_plate} · Km: {anomaly.km_driven.toFixed(0)}</div>
              <div>Previsto: {anomaly.expected_liters.toFixed(1)} L · Effettivo: {anomaly.actual_liters.toFixed(1)} L</div>
              <div className="font-bold">Eccesso: +{anomaly.excess_liters.toFixed(1)} L</div>
            </div>
          </div>
        </div>
      )}

      {/* Anomaly km (deviazione >= 20% dalla media storica del mezzo) */}
      {job.km_anomaly && (
        <div className="mt-3 border-2 border-amber-300 bg-amber-50 rounded-xl p-4 flex items-start gap-3" data-testid="km-anomaly-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-amber-900">
              🚐 Anomalia km rilevata — {job.km_anomaly.deviation_pct >= 0 ? "+" : ""}{job.km_anomaly.deviation_pct.toFixed(1)}% {job.km_anomaly.direction} la media
            </div>
            <div className="text-amber-800 mt-1 text-xs font-mono space-y-0.5">
              <div>Mezzo: <b>{job.km_anomaly.vehicle_plate}</b> · Km percorsi: <b>{job.km_anomaly.actual_km.toFixed(0)}</b></div>
              <div>Media storica: <b>{job.km_anomaly.median_km.toFixed(0)} km</b> (su {job.km_anomaly.samples} lavori)</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
